import React, { useState, FC } from 'react';
import { Box, Avatar, Text, AvatarSize, Icon } from '@bechtel/gds';
import { ProgressWeldBoxStyled } from './styled';
import { Trans } from '@lingui/macro';
import { useName } from '@bechtel/client-common';
import { WeldItem } from '../weld-item';
import { AssignedWeldSidesheet } from '@bechtel/client-weld-common';
import { RodRequest, RodRequestWeld } from '@bechtel/types';
import { map } from 'lodash';
import { ProgressDetail, RodRequestProgressWeld } from '../../types';

interface ProgressWeldProps {
  progressDetail: ProgressDetail;
  requestNumber: string;
  rodRequestProgressWeld: RodRequestProgressWeld[];
}

export const ProgressWeld: FC<ProgressWeldProps> = ({ progressDetail, requestNumber, rodRequestProgressWeld }) => {
  const { getFullName } = useName();
  const [showSidesheet, setShowSidesheet] = useState(false);
  const [selectedWelds, setSelectedWelds] = useState<RodRequestWeld[]>([]);
  const { totalCaddies, returnedCaddies, welder } = progressDetail;

  const onCloseSidesheet = () => {
    setShowSidesheet(false);
  };

  const onSave = (rodRequests: RodRequest[]) => {
    setSelectedWelds(rodRequests[0].stencils[0].welds);
  };

  return (
    <>
      <ProgressWeldBoxStyled>
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'space-between' }}
          p={{ sm: '500' }}
          data-testid="progress-weld-details-header"
        >
          <Box>
            <Avatar size={AvatarSize.MEDIUM} src={welder?.imageUrl} />
            <Box
              flexDirection={{ sm: 'column' }}
              ml={{ sm: '300' }}
              css={{ lineHeight: '1.3' }}
              data-testid="welder-details-container"
            >
              <Text weight={500} size="500" color="grey.600">
                {getFullName(welder?.firstName, welder?.lastName)}
              </Text>
              <Text size="300" color="grey.600">
                {welder?.badgeNumber}
              </Text>
            </Box>
          </Box>
          <Box alignItems={{ sm: 'flex-end' }} flexDirection={{ sm: 'column' }} data-testid="return-status">
            <Text weight={600} size="500" color="blue.700">
              {`(${returnedCaddies}/${totalCaddies})`}
            </Text>
            <Box mt={{ sm: '200' }}>
              {totalCaddies === returnedCaddies && <Icon icon="check-circle" fillColor="green.500" />}
              &nbsp;
              <Text weight={400} size="400" color="blue.700">
                <Trans>Returned</Trans>
              </Text>
            </Box>
          </Box>
        </Box>
        <Box
          css={{ background: 'white' }}
          px={{ sm: '500' }}
          py={{ sm: '450' }}
          justifyContent={{ sm: 'space-between' }}
          flexDirection={{ sm: 'column' }}
          data-testid="weld-list-container"
        >
          <Box justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }} pb={{ sm: '400' }}>
            <Box flexDirection={{ sm: 'column' }} css={{ lineHeight: '1.4' }}>
              <Text color="grey.1000" weight="600" size="400">
                <Trans>Welds</Trans>
              </Text>
              <Text color="grey.600" weight="400" size="300">
                {selectedWelds.length} <Trans>Selected</Trans>
              </Text>
            </Box>

            <Box alignItems={{ sm: 'center' }} css={{ cursor: 'pointer' }} onClick={() => setShowSidesheet(true)}>
              <Text color="grey.600" weight="400" size="300">
                <Trans>Select</Trans>
              </Text>
              &nbsp;&nbsp;&nbsp;
              <Icon icon="edit" fillColor="bechtel.blue.800" />
            </Box>
          </Box>
          {selectedWelds.length ? (
            <Box
              p={{ sm: '400' }}
              flexDirection={{ sm: 'column' }}
              css={`
                ${({ theme }) => `border-top: 1px solid ${theme.colors.grey[300]}`}
              `}
            >
              {map(selectedWelds, (rodRequestWeld) => (
                <WeldItem rodRequestWeld={rodRequestWeld} key={rodRequestWeld.id} />
              ))}
            </Box>
          ) : null}
        </Box>
      </ProgressWeldBoxStyled>
      {showSidesheet && (
        <AssignedWeldSidesheet
          welder={welder}
          onClose={onCloseSidesheet}
          onSave={onSave}
          requestNumber={requestNumber}
          rodRequestProgressWeld={rodRequestProgressWeld}
        />
      )}
    </>
  );
};
